import axios from "axios";
import { CONFIG } from "../../config";
import { i18n } from "../../i18n";
import { ClientConfig } from "../../clientSpecificConfig";
import {
  SHOW_LOADER,
  HIDE_LOADER,
  ACTIVE_TAB,
  APP_ERROR,
  CLEAR_APP_ERROR,
  SET_LOGIN_ATTEMPTS,
  INIT_LOGIN_ATTEMPTS,
  SET_LANGUAGELIST_FOR_CLIENT,
  SET_SELECTED_LANGUAGE,
  CLEAR_LOGIN_ATTEMPTS,
  SET_CLIENT_CONFIG,
  GET_NOTIFICATIONS,
  UPDATE_ACCESSIBILITY_CLASS_LIST,
  UPDATE_ACCESSIBILITY_IS_ACTIVE,
  SET_SUSIE_LOADED,
  SET_SUSIE_BANNER_VISIBLE,
  GET_SUSIE_SCRIPT_TAG,
} from "../actions/common";
const getClientFromLocalUrl = () => {
  const url = window.location.href; // Get full URL
  const hashIndex = url.indexOf("?");

  if (hashIndex === -1) return null; // No query parameters

  const queryString = url.substring(hashIndex); // Extract query string
  const params = new URLSearchParams(queryString);

  return params.get("client"); // Get the client parameter
};
// Initialize client config from URL or localStorage on page load
let initialClientCfg = {};

const getInitialClientConfig = () => {
  const subdomains = window.location.host.split("-");
  let clientAbbr = "cambium";

  // Get client abbreviation from the URL
  if (subdomains.length > 1) {
    clientAbbr = subdomains[0].toLowerCase();
  } else if (window.location.hostname.indexOf("localhost") >= 0) {
    // First check URL querystring
    clientAbbr = getClientFromLocalUrl();

    // If not in URL, check localStorage
    if (!clientAbbr) {
      // For local development, check localStorage
      const clientAbbrFromStorage = localStorage.getItem("clientAbbr");
      if (clientAbbrFromStorage) {
        clientAbbr = clientAbbrFromStorage;
      }
    }
  }

  // Return config if it exists for this client
  if (ClientConfig[clientAbbr]) {
    console.log("Store initialization - clientname:", ClientConfig[clientAbbr].clientname);
    if (typeof document !== "undefined" && document.body) {
      document.body.classList.add(ClientConfig[clientAbbr].clientname.toLowerCase());
    }
    return ClientConfig[clientAbbr];
  }

  return {};
};

initialClientCfg = getInitialClientConfig();

// Load notifications from sessionStorage if available
const loadInitialNotifications = () => {
  try {
    // Get locale from localStorage (same as i18n initialization)
    const locale = localStorage.getItem("locale") || "en";
    const cachedNotifications = sessionStorage.getItem("notifications" + locale);
    if (cachedNotifications) {
      const notifications = JSON.parse(cachedNotifications);
      if (notifications && notifications.length > 0) {
        console.log(`Loading notifications from cache for language: ${locale}`);
        return notifications;
      }
    }
  } catch (e) {
    console.error("Error loading notifications from sessionStorage:", e);
  }
  return [];
};

const state = {
  isLoading: false,
  clientCfg: initialClientCfg,
  errorMessage: "",
  loginAttempts: {},
  languages: {},
  activeTab: "",
  selectedLanguage: "en",
  notifications: loadInitialNotifications(),
  accessibilityClassList: "",
  accessibilityIsActive: false,
  // Check if SUSIE script exists in DOM (persists across page refresh)
  susieLoadedInSession: !!document.getElementById("cai-innov-apps-config"),
  susieBannerVisible: false,
};

const getters = {
  isLoading: (state) => state.isLoading,
  getErrorMessage: (state) => state.errorMessage,
  loginAttempts: (state) => state.loginAttempts,
  languages: (state) => state.languages,
  getActiveTab: (state) => state.activeTab,
  selectedLanguage: (state) => state.selectedLanguage,
  getClientConfig: (state) => state.clientCfg,
  getNotifications: (state) => state.notifications,
  accessibilityClassList: (state) => state.accessibilityClassList,
  accessibilityIsActive: (state) => state.accessibilityClassList,
  susieLoadedInSession: (state) => state.susieLoadedInSession,
  susieBannerVisible: (state) => state.susieBannerVisible,
};

const actions = {
  [SHOW_LOADER]: ({ commit }) => {
    commit(SHOW_LOADER);
  },
  [HIDE_LOADER]: ({ commit }) => {
    commit(HIDE_LOADER);
  },
  [ACTIVE_TAB]: ({ commit }, tab) => {
    commit(ACTIVE_TAB, tab);
  },
  [APP_ERROR]: ({ commit }, err) => {
    commit(APP_ERROR, err);
  },
  [CLEAR_APP_ERROR]: ({ commit }) => {
    commit(CLEAR_APP_ERROR);
  },
  [SET_LOGIN_ATTEMPTS]: ({ commit }, loginAttempts) => {
    commit(SET_LOGIN_ATTEMPTS, loginAttempts);
  },
  [SET_SELECTED_LANGUAGE]: ({ commit }, selectedLang) => {
    commit(SET_SELECTED_LANGUAGE, selectedLang);
  },
  [SET_LANGUAGELIST_FOR_CLIENT]: ({ commit }, client) => {
    if (client == null) return;
    axios({
      url: CONFIG.BACKEND_URL + "api/I18n/languages",
      data: Object.assign({}, { clientName: client }),
      method: "POST",
    })
      .then((response) => {
        commit(SET_LANGUAGELIST_FOR_CLIENT, response.data);
      })
      .catch((err) => {
        console.log("SET_LANGUAGELIST_FOR_CLIENT errored", err);
      });
  },
  [GET_NOTIFICATIONS]: ({ commit }, { clientName, lang }) => {
    var notifications = JSON.parse(sessionStorage.getItem("notifications" + lang));
    if (!notifications || notifications.length == 0) {
      axios({
        // url: CONFIG.BACKEND_URL + `api/I18n/notifications`,
        // data: Object.assign({}, { clientName: client }),
        url: CONFIG.BACKEND_URL + `api/I18n/notifications`,
        data: {
          client: clientName,
          locale: lang,
        },
        method: "POST",
      })
        .then((response) => {
          commit(GET_NOTIFICATIONS, response.data);
          sessionStorage.setItem("notifications" + lang, JSON.stringify(response.data));
        })
        .catch((err) => {
          console.log("GET_NOTIFICATIONS errored", err);
        });
    } else commit(GET_NOTIFICATIONS, notifications);
  },
  [INIT_LOGIN_ATTEMPTS]: ({ commit }) => {
    commit(INIT_LOGIN_ATTEMPTS);
  },
  [CLEAR_LOGIN_ATTEMPTS]: ({ commit }) => {
    commit(CLEAR_LOGIN_ATTEMPTS);
  },
  [SET_CLIENT_CONFIG]: ({ commit }, client) => {
    commit(SET_CLIENT_CONFIG, client);
  },
  [UPDATE_ACCESSIBILITY_CLASS_LIST]: ({ commit }) => {
    const classList = window.cambiumAccessibilityWidgetGetClassListFromState();
    commit(UPDATE_ACCESSIBILITY_CLASS_LIST, classList.join(","));
  },
  [UPDATE_ACCESSIBILITY_IS_ACTIVE]: ({ commit }) => {
    const isActive = window.cambiumAccessibilityWidgetAnyToolsActive();
    commit(UPDATE_ACCESSIBILITY_IS_ACTIVE, isActive);
  },
  [SET_SUSIE_LOADED]: ({ commit }, loaded) => {
    commit(SET_SUSIE_LOADED, loaded);
  },
  [SET_SUSIE_BANNER_VISIBLE]: ({ commit }, visible) => {
    commit(SET_SUSIE_BANNER_VISIBLE, visible);
  },
  [GET_SUSIE_SCRIPT_TAG]: async () => {
    const token = sessionStorage.getItem("jwttoken");
    if (!token) {
      console.error("No JWT token available for SUSIE");
      return null;
    }

    try {
      const response = await axios.get(`${CONFIG.BACKEND_URL}api/susie/script-tag`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data || null;
    } catch (error) {
      console.error("Error fetching SUSIE script tag:", error);
      return null;
    }
  },
};

const mutations = {
  [SHOW_LOADER]: (state) => {
    state.isLoading = true;
  },
  [HIDE_LOADER]: (state) => {
    state.isLoading = false;
  },
  [ACTIVE_TAB]: (state, tab) => {
    state.activeTab = tab;
  },
  [CLEAR_APP_ERROR]: (state) => {
    state.errorMessage = "";
  },
  [APP_ERROR]: (state, message) => {
    state.isLoading = false;
    state.errorMessage = message;
    if (message === i18n.global.t("AccessCode.notFound")) return;

    let useGenericErrorMessage = true;
    if (message.response) {
      if (message.response.data.message !== undefined) {
        state.errorMessage = message.response.data.message;
        useGenericErrorMessage = false;
      }
    }
    if (useGenericErrorMessage) {
      state.errorMessage = "ui.error.service_unavailable";
    }
  },
  [INIT_LOGIN_ATTEMPTS]: (state) => {
    var loginObj = Object.assign({}, { timestamp: Date.now(), count: 0 });
    if (!localStorage.getItem("loginAttempts")) {
      localStorage.setItem("loginAttempts", JSON.stringify(loginObj));
      state.loginAttempts = loginObj;
    } else {
      state.loginAttempts = JSON.parse(localStorage.getItem("loginAttempts"));
    }
  },
  [SET_LOGIN_ATTEMPTS]: (state) => {
    var loginObj = Object.assign({}, { timestamp: Date.now(), count: 1 });
    if (localStorage.getItem("loginAttempts")) {
      loginObj = JSON.parse(localStorage.getItem("loginAttempts"));
      loginObj.timestamp = Date.now();
      loginObj.count = loginObj.count + 1;
    }
    localStorage.setItem("loginAttempts", JSON.stringify(loginObj));
    state.loginAttempts = loginObj;
  },
  [SET_SELECTED_LANGUAGE]: (state, selectedLang) => {
    localStorage.setItem("locale", selectedLang);
    if (selectedLang) {
      state.selectedLanguage = selectedLang;
    }
  },
  [GET_NOTIFICATIONS]: (state, notifications) => {
    state.notifications = notifications;
  },
  [SET_LANGUAGELIST_FOR_CLIENT]: (state, languagesList) => {
    state.languages = languagesList;
  },
  [CLEAR_LOGIN_ATTEMPTS]: (state) => {
    var loginObj = JSON.stringify(Object.assign({}, { timestamp: Date.now(), count: 0 }));
    localStorage.setItem("loginAttempts", loginObj);
    state.loginAttempts = Object.assign({}, { timestamp: Date.now(), count: 0 });
  },
  [SET_CLIENT_CONFIG]: (state, clientcfg) => {
    // Remove old client class if it exists
    if (state.clientCfg?.clientname && typeof document !== "undefined" && document.body) {
      document.body.classList.remove(state.clientCfg.clientname.toLowerCase());
    }

    state.clientCfg = clientcfg;

    // Add new client class
    if (clientcfg?.clientname && typeof document !== "undefined" && document.body) {
      document.body.classList.add(clientcfg.clientname.toLowerCase());
    }
  },
  [UPDATE_ACCESSIBILITY_CLASS_LIST]: (state, classList) => {
    state.accessibilityClassList = classList;
  },
  [UPDATE_ACCESSIBILITY_IS_ACTIVE]: (state, isActive) => {
    state.accessibilityIsActive = isActive;
  },
  [SET_SUSIE_LOADED]: (state, loaded) => {
    state.susieLoadedInSession = loaded;
  },
  [SET_SUSIE_BANNER_VISIBLE]: (state, visible) => {
    state.susieBannerVisible = visible;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
